import { createStore, applyMiddleware } from 'redux';
import thunkMiddleware from 'redux-thunk';
import createLogger from 'redux-logger';
import api from 'middlewares/api';
import oldApi from 'containers/App/screens/Tube/utils/oldApi';
import { isJsonString } from 'utils/index';
import promiseMiddleware from 'redux-promise-middleware';
import { Iterable } from 'immutable';
import queryString from 'query-string';
import { defaultLocale, allowLocales } from 'configs/locale.js';
import { find } from 'lodash';
import axios from 'axios';
import { camelizeKeys, pascalizeKeys } from 'humps';
import { permissionMiddleware } from '@mayo/user-permissions';
import * as Sentry from '@sentry/browser';

export const REQUEST = 'REQUEST';
export const SUCCESS = 'SUCCESS';
export const FAIL = 'FAIL';

let createStoreWithMiddleware = createStore;

if (process.env.PROJECT_ENV === 'production') {
  Sentry.init({
    dsn: 'https://5e70a69865ce41abb3c143b609856114@sentry.io/1492397',
    release: 'fd@0.0.2',
    beforeSend(event, hint) {
      let httpStatus = null;
      if (hint.originalException && hint.originalException.response) {
        httpStatus = hint.originalException.response.status;
      }
      const excludeStatus = [401, 403];
      if (httpStatus && excludeStatus.indexOf(httpStatus) > -1) {
        return null;
      }
      return event;
    },
  });
  createStoreWithMiddleware = applyMiddleware(
    permissionMiddleware,
    api,
    oldApi,
    thunkMiddleware,
    promiseMiddleware({
      promiseTypeSuffixes: [REQUEST, SUCCESS, FAIL],
    })
  )(createStore);
} else {
  const loggerMiddleware = createLogger({
    stateTransformer: (state) => {
      const newState = {};
      for (const key of Object.keys(state)) {
        if (Iterable.isIterable(state[key])) {
          newState[key] = state[key].toJS();
        } else {
          newState[key] = state[key];
        }
      }
      return newState;
    },
  });

  createStoreWithMiddleware = applyMiddleware(
    permissionMiddleware,
    api,
    oldApi,
    thunkMiddleware,
    promiseMiddleware({
      promiseTypeSuffixes: [REQUEST, SUCCESS, FAIL],
    }),
    loggerMiddleware
  )(createStore);
}

export default function configureStore(
  rootReducer,
  moduleType,
  baseURL,
  initialState
) {
  const queryStrings = queryString.parse(location.search);
  let locale =
    (queryStrings.lang && queryStrings.lang !== 'undefined'
      ? queryStrings.lang
      : undefined) ||
    sessionStorage.locale ||
    localStorage.locale ||
    navigator.language;
  locale = locale ? locale.toLowerCase() : locale;
  if (find(allowLocales, { code: locale }) === undefined) {
    locale = defaultLocale;
  }
  sessionStorage.locale = locale;
  localStorage.setItem('locale', locale);

  // axios 設定
  axios.defaults.baseURL = baseURL;
  axios.defaults.headers.common['Accept-Language'] = locale;
  axios.defaults.withCredentials = true;
  axios.defaults.transformResponse = (data, header) => {
    if (header['content-type'].indexOf('application/json') > -1) {
      if (isJsonString(data)) {
        return camelizeKeys(JSON.parse(data));
      }
      return camelizeKeys(data);
    }
    return data;
  };

  axios.defaults.transformRequest = (data) => {
    if (typeof data === 'object' && !(data instanceof FormData)) {
      return JSON.stringify(pascalizeKeys(data));
    }
    return data;
  };
  axios.defaults.responseType = 'json';
  axios.defaults.headers.post['Content-Type'] = 'application/json';
  axios.defaults.headers.put['Content-Type'] = 'application/json';
  axios.defaults.headers.delete['Content-Type'] = 'application/json';

  let appState = {
    app: {
      application: {
        moduleType,
        isLoading: false,
        isLoaded: false,
        locale,
        RKey: queryStrings.code,
        red: queryStrings.red ? queryStrings.red : location.host + '/Tube',
        accessToken: sessionStorage.access_token,
        employeeId: sessionStorage.employee_id,
        companyId: sessionStorage.company_id,
        isLoggedIn: window.localStorage.getItem('isCookie') !== null,
        isOtherAccountLoggedIn: false,
        progresses: [],
        locales: allowLocales,
        headers: {
          'Accept-Language': locale,
        },
        theme: {
          companyLogo: null,
          headerColor: null,
        },
        isPreviewMode: queryStrings.ipm === 's',
      },
    },
  };

  appState = Object.assign(appState, initialState);

  return createStoreWithMiddleware(rootReducer, appState);
}



/** WEBPACK FOOTER **
 ** D:/a/1/s/src/containers/App/configureStore.js
 **/